import {useTask} from "@/core/composable/useTask";
import {useAlerts} from "@/core/store/alerts";
import {useBus} from "@/core/store/bus";
import {Maybe} from "@/core/types";
import {arrayGroup} from "@/core/util/arrayGroup";
import {arrayIndex} from "@/core/util/arrayIndex";
import {sleep} from "@/core/util/sleep";
import {BasketItem} from "@/gql/gen";
import {useBasketItemModel} from "@/modules/shop/composable/useBasketItemModel";
import {ProductModel} from "@/modules/shop/composable/useProduct";
import {BasketItemModel, ReactiveBasketItemModel,} from "@/modules/shop/composable/util";
import {useCatalogStore} from "@/modules/shop/store/catalog";
import {useProductModificatorStore} from "@/modules/shop/store/product-modificator";
import {useProductModificatorsComputedStore} from "@/modules/shop/store/product-modificators-computed";
import {
    BasketCalculationMode,
    BasketItemDescriptor,
    BasketItemStatus,
    fillBasketItemByProduct,
    TBasketOp,
    TBasketOpAdd,
    TBasketOpDelete,
    TBasketOpSetQuantity,
} from "@/modules/shop/store/util/basket";
import {useDebounceFn} from "@vueuse/core";
import debounce from "lodash-es/debounce";
import {defineStore, storeToRefs} from "pinia";
import {computed, nextTick, ref, shallowRef, toValue, triggerRef, watch} from "vue";

const STORE_NAME = "shop-basket";

export const useBasketStore = defineStore(STORE_NAME, () => {
    const {bus} = useBus();

    const catalogStore = useCatalogStore();
    const productModificatorStore = useProductModificatorStore();
    const productModificatorsComputedStore = useProductModificatorsComputedStore();
    const {showFlash} = useAlerts();

    const itemsSource = ref<BasketItem[]>([])
    const items = shallowRef<ReactiveBasketItemModel[]>([])
    const {productModificatorsById} = storeToRefs(productModificatorStore);

    const {ensureProduct, ensureProductsById} = catalogStore;

    // STATE

    const changedAt = ref<number>(0);
    const syncedChangedAt = ref<number>(0);
    const calculationMode = ref<BasketCalculationMode>("client");
    const pendingSync = ref(false);

    // GETTERS

    const itemsByProduct = computed(() => arrayGroup(items.value, (item) => item.fields.PRODUCT_ID))
    const itemByProduct = computed(() => arrayIndex(items.value, (item) => item.fields.PRODUCT_ID))
    const itemsRoot = computed(() => items.value.filter((item) => item.getIsRoot()))
    const itemsRaw = computed(() => items.value.map((item) => item.fields))

    const itemsWatchable = computed(() =>
        itemsRaw.value.map((item) => ({
            ID: item.ID,
            QUANTITY: item.QUANTITY,
            PROPERTIES: item.PROPERTIES,
            MODIFICATORS: item.MODIFICATORS,
        }))
    );

    const basketTotal = computed(() => {
        return items.value.reduce((acc, item) => {
            acc += item.getPriceTotal();
            return acc;
        }, 0)
    })


    const synced = computed(() => changedAt.value <= syncedChangedAt.value);
    const price = computed(() =>
        items.value.reduce((acc, item) => ((acc += item.getPriceTotal()), acc), 0)
    );
    const priceLevel = computed(() => Math.round(price.value / 1500));
    const isEmpty = computed(() => !itemsRaw.value.length);
    const isNotEmpty = computed(() => !!itemsRaw.value.length);
    const weight = computed(() =>
        items.value.reduce((acc, item) => {
            acc += (item.product.PROPERTIES.WEIGHT || 0) * item.fields.QUANTITY;
            return acc;
        }, 0)
    );

    /**
     * Получает статистику по товарам в корзине для проверки ограничений
     */
    const basketItemsStat = computed(() => {
        const productIds: number[] = [];
        const sectionIds: number[] = [];
        const products: Record<number, any> = {};
        const sections: Record<number, any> = {};

        items.value.forEach((item) => {
            if (!item.isRoot.value) return;

            const productId = item.fields.PRODUCT_ID;
            const product = item.product;

            if (productId && !productIds.includes(productId)) {
                productIds.push(productId);
                products[productId] = {
                    ID: productId,
                    NAME: product.NAME || ''
                };
            }

            if (product.IBLOCK_SECTION_IDS) {
                product.IBLOCK_SECTION_IDS.forEach((sectionId) => {
                    if (!sectionIds.includes(sectionId)) {
                        sectionIds.push(sectionId);
                        // Получаем название секции из product.SECTION или PATH
                        const section = product.SECTION || product.PATH?.find(s => s?.ID === sectionId);
                        sections[sectionId] = {
                            ID: sectionId,
                            NAME: section?.NAME || ''
                        };
                    }
                });
            }
        });

        return {
            productIds,
            sectionIds,
            products,
            sections
        };
    });

    /**
     * Валидация лимитов для товаров, добавленных через модификаторы
     *
     * Архитектура:
     * - Связанные товары - обычные элементы корзины (БЕЗ PARENT_ID), но с REASON_MOD_ID
     * - Они НЕ множатся для каждого основного товара, а группируются
     * - Лимит = QUANTITY_LIMIT * суммарное_количество_всех_основных_товаров_с_этим_модификатором
     *
     * Ключ для группировки: modId_productId
     *
     * Пример:
     * - 2 пиццы с модификатором gift_drink (QUANTITY_LIMIT=1)
     * - 1 элемент "Напиток" в корзине (REASON_MOD_ID=gift_drink)
     * - Лимит: 1 * 2 = 2 шт
     */
    const modificatorsStat = computed<Record<string, BasketItemStatus>>(() => {
        const basketEntries: Record<string, BasketItemStatus> = {};

        // Шаг 1: Считаем количество основных товаров для каждого модификатора
        const modificatorsQuantity: Record<number, number> = {};

        for (const item of items.value) {
            // Только основные товары (нет REASON_MOD_ID и нет PARENT_ID)
            if (item.getIsAddedByModificator())
                continue;

            // Проходим по модификаторам товара
            for (const modId of item.product.PROPERTIES.MODIFICATORS) {
                if (!modificatorsQuantity[modId]) {
                    modificatorsQuantity[modId] = 0;
                }
                modificatorsQuantity[modId] += item.fields.QUANTITY;
            }
        }

        // Шаг 2: Валидируем связанные товары
        for (const item of items.value) {
            // Только связанные товары (есть REASON_MOD_ID)
            const modId = item.fields.PROPERTIES.REASON_MOD_ID;
            if (!modId) continue;

            // Пропускаем дочерние элементы (если есть - это не связанные через модификатор)
            if (item.fields.PARENT_ID) continue;

            const mod = productModificatorsById.value[modId];
            if (!mod) continue;

            const productId = item.fields.PRODUCT_ID;
            const option = mod.OPTIONS.find((opt) => opt.PRODUCT_ID === productId);
            if (!option) continue;

            // Уникальный ключ: modId_productId
            const key = `${modId}_${productId}`;

            if (!basketEntries[key]) {
                // Рассчитываем лимит:
                // QUANTITY_LIMIT * суммарное количество основных товаров с этим модификатором
                const mainItemsQuantity = modificatorsQuantity[modId] || 0;
                const quantityLimit =
                    option.QUANTITY_LIMIT || mod.QUANTITY_LIMIT || Infinity;
                const totalLimit =
                    quantityLimit === Infinity
                        ? Infinity
                        : quantityLimit * mainItemsQuantity;

                basketEntries[key] = {
                    mod,
                    quantity: 0,
                    quantityLimit: totalLimit,
                    quantityAvailable: totalLimit,
                    haveError: false,
                };
            }

            // Суммируем количество (может быть несколько элементов с одним ключом, хотя не должно)
            basketEntries[key]!.quantity += item.fields.QUANTITY;
        }

        // Шаг 3: Пересчитываем quantityAvailable и haveError
        for (const status of Object.values(basketEntries)) {
            status.quantityAvailable =
                status.quantityLimit === Infinity
                    ? Infinity
                    : status.quantityLimit - status.quantity;
            status.haveError = status.quantityAvailable < 0;
        }

        return basketEntries;
    });


    // ACTIONS

    /**
     * Удалить или уменьшить товары, превышающие лимиты модификаторов
     *
     * Проверяет только связанные товары (REASON_MOD_ID !== null)
     * Алгоритм:
     * 1. Если quantityLimit === 0 - удаляем товар (нет основных товаров)
     * 2. Если quantityAvailable < 0 - уменьшаем до quantityLimit
     *
     * Примечание: Проверка глобальных модификаторов вынесена в отдельный watch
     */
    function removeExcessItems() {
        const changed: string[] = [];
        const removed: string[] = [];

        for (const item of items.value) {

            // Проверяем только связанные товары
            if (!item.fields.PROPERTIES.REASON_MOD_ID) continue;
            if (item.fields.PARENT_UUID) continue; // Пропускаем дочерние элементы

            const status = toValue(item.status);

            if (status.quantityLimit === 0) {
                // Нет основных товаров - удаляем связанный товар
                removed.push(item.fields.NAME || "Товар");
                item.remove();
            } else if (status.quantityAvailable < 0) {
                // Превышение лимита - уменьшаем до разрешенного
                changed.push(
                    (item.fields.NAME || "Товар") + " до х" + status.quantityLimit
                );
                item.setQuantity(status.quantityLimit, {silent: true}); // silent - автоматическая коррекция
            }
        }

        if (changed.length || removed.length) {
            let message = "";
            if (changed.length) {
                message += "<div>Изменено количество:</div>";
                message += "<ul><li>" + changed.join("</li><li>") + "</li></ul>";
            }
            if (removed.length) {
                message += "<div>Удалены (нет основных товаров):</div>";
                message += "<ul><li>" + removed.join("</li><li>") + "</li></ul>";
            }
            showFlash({
                message,
            });
        }
    }

    function getItem(item: BasketItemDescriptor) {
        if (!item) {
            return;
        } else if (typeof item === "number") {
            return items.value.find(basketItem => basketItem.fields.ID === item)
        } else if (typeof item === "string") {
            return items.value.find(basketItem => basketItem.fields.UUID === item)
        } else if (typeof item === "object") {
            if ("_isModel" in item) {
                return item
            } else {
                return items.value.find(basketItem => basketItem.fields.UUID === item.UUID)
            }
        }
    }

    function getItemId(item: BasketItemDescriptor) {
        if (!item) {
            return;
        } else if (typeof item === "number") {
            return item;
        } else if (typeof item === "string") {
            const foundItem = items.value.find(basketItem => basketItem.fields.UUID === item)
            return foundItem?.id
        } else if ("_isModel" in item) {
            return item.fields.ID
        } else {
            return item.ID;
        }
    }

    function getItemUuid(item: BasketItemDescriptor) {
        if (!item) {
            return;
        } else if (typeof item === "number") {
            const foundItem = items.value.find(basketItem => basketItem.fields.ID === item)
            return foundItem?.getUuid()
        } else if (typeof item === "string") {
            const foundItem = items.value.find(basketItem => basketItem.getUuid() === item)
            return foundItem?.getUuid()
        } else if ("_isModel" in item) {
            return item.getUuid()
        } else {
            return item.UUID
        }
    }

    /**
     * Валидация лимитов модификаторов перед добавлением в корзину
     * Обрезает лишние опции если превышен LIMIT
     */
    function validateModificatorsLimits(
        item: BasketItem | BasketItemModel,
        product: ProductModel
    ) {
        const itemFields = "_isModel" in item ? item.fields : item;

        // Получаем модификаторы товара
        const productModificators =
            productModificatorStore.productModificatorsByProductId[product.ID] || [];

        // Проверяем каждый модификатор
        for (const modificator of productModificators) {
            const groupCode = modificator.GROUP_CODE;
            const modValue = itemFields.MODIFICATORS[groupCode];

            if (!modValue || typeof modValue !== "object") continue;

            const selectedUids = Object.keys(modValue);
            const limit = modificator.LIMIT;

            // Если LIMIT задан и превышен
            if (limit > 0 && selectedUids.length > limit) {
                // Оставляем только первые LIMIT опций
                const allowedUids = selectedUids.slice(0, limit);
                const newValue: Record<string, number> = {};

                for (const uid of allowedUids) {
                    newValue[uid] = modValue[uid];
                }

                itemFields.MODIFICATORS[groupCode] = newValue;
            }
        }
    }

    async function addItem(
        item: BasketItem | BasketItemModel,
        product: ProductModel
    ) {
        const itemModel = useBasketItemModel(item, product);
        items.value.push(itemModel);
        itemModel.setCommitted();
        // Синхронизируем связанные товары после добавления основного товара
        await itemModel.syncChildren();
        triggerRef(items);
        return itemModel;
    }

    watch(items, () => {
        itemsSource.value = items.value.map((item) => item.fields);
    }, {deep: true});

    const fillBasket = (itemsData: BasketItem[]) => {
        items.value = itemsData.map((item) => {
            const product = catalogStore.productsModelsById[item.PRODUCT_ID]
            if (product) {
                const itemModel = useBasketItemModel(item, product);
                itemModel.commited.value = true
                return itemModel
            }
        }).filter(Boolean) as BasketItemModel[]

        triggerRef(items)
    }

    /**
     * Добавить товар в корзину по правилу корзины (special)
     *
     * Товары добавляются с привязкой к правилу через REASON_RULE_ID
     * Используется для товаров по специальной цене (special)
     *
     * @param productId - ID товара
     * @param quantity - Количество
     * @param ruleId - ID правила корзины
     * @param ruleCode - CODE правила корзины
     */
    async function addItemByRule(
        productId: number,
        quantity: number,
        ruleId: number,
        ruleCode: string | null
    ) {
        const product = await ensureProduct(productId);
        if (!product) {
            console.error('[BasketStore] Product not found:', productId);
            return null;
        }

        // Проверяем, нет ли уже такого товара с таким же правилом
        const existingItem = items.value.find(
            item =>
                item.fields.PRODUCT_ID === productId &&
                item.fields.PROPERTIES.REASON_RULE_ID === ruleId
        );

        if (existingItem) {
            // Увеличиваем количество существующего товара
            await existingItem.setQuantity(existingItem.fields.QUANTITY + quantity);
            return existingItem;
        }

        // Создаем новый элемент корзины с привязкой к правилу
        const item = fillBasketItemByProduct(
            {
                QUANTITY: quantity,
                PROPERTIES: {
                    REASON_RULE_ID: ruleId,
                    REASON_RULE_CODE: ruleCode,
                }
            },
            product,
            undefined, // reasonModificator - не используется для правил
            undefined  // reasonRule - передаем через PROPERTIES напрямую
        );

        return await addItem(item, product);
    }

    function removeItems(itemsDescriptors: BasketItemDescriptor[]) {
        let itemsToRemove = itemsDescriptors.map(item => getItem(item)).filter(Boolean);
        if (!itemsToRemove.length) return
        itemsToRemove = itemsToRemove.concat(itemsToRemove.map(item => getChildren(item!)).flat())
        items.value = items.value.filter((item) => !itemsToRemove.includes(item))
        triggerRef(items);
    }

    function removeItem(item: BasketItemDescriptor) {
        removeItems([item])
        triggerRef(items)
    }

    function getChildren(item: BasketItemDescriptor) {
        let foundItem = getItem(item)
        if (!foundItem) return [];
        return items.value.filter((item) => item.getParentUuid() === foundItem!.getUuid())
    }

    const fill = async (newItems: BasketItem[], preserveItems = false) => {
        if (!preserveItems) {
            const productsById = await ensureProductsById(
                newItems.map((item) => item.ELEMENT || item.PRODUCT_ID)
            );
            items.value = newItems.map((item) =>
                useBasketItemModel(item, productsById[item.PRODUCT_ID]!)
            );
        } else {
            items.value.forEach((oldItem) => {
                newItems.forEach((newItem) => {
                    if (
                        oldItem.fields.ID === newItem.ID ||
                        oldItem.fields.UUID === newItem.UUID
                    ) {
                        oldItem.updateFromServer(newItem);
                    }
                });
            });
        }
        //syncedChangedAt.value = data.CLIENT_CHANGED_AT || 0
        //syncedChangedAt.value = Date.now()
    };

    const itemIndex = ref(0);

    const sync = useTask(async () => {
        const v = changedAt.value;
        const serverItems = items.value.map((item) => ({
            ID: item.fields.ID < 0 ? itemIndex.value++ : item.fields.ID,
            ...item.getFakeSync(),
        }));
        await sleep(3000);
        await fill(serverItems, true);
        syncedChangedAt.value = v;
    });

    const save = useTask(async () => {
        await sleep(3000);
    });

    const queueSync = useDebounceFn(async () => {
        pendingSync.value = true;
        if (sync.isProcessing.value) return;
        pendingSync.value = false;
        await sync.run();
        if (pendingSync.value) {
            await queueSync();
        }
    }, 500);

    const queueSave = useDebounceFn(() => save.run(), 10000);

    const findItemModel = (
        data: Omit<TBasketOpAdd | TBasketOpSetQuantity | TBasketOpDelete, "action">
    ): ReactiveBasketItemModel | undefined => {
        const {item, itemId, itemUuid, productId} = data;
        return items.value.find((basketItem) => {
            if (item) {
                return basketItem === item || basketItem.isEqualWithItem(item)
            } else if (itemId) {
                return itemId === basketItem.id;
            } else if (itemUuid) {
                return itemUuid === basketItem.getUuid();
            } else if (productId) {
                return productId === basketItem.fields.PRODUCT_ID;
                //&& (!basketPropsHash || item.INPUT_PROPS_HASH === basketPropsHash)
            }
        });
    };

    const op = async (
        data: TBasketOp
    ): Promise<Maybe<ReactiveBasketItemModel>> => {
        let itemModel: BasketItemModel | ReactiveBasketItemModel | undefined | null;

        switch (data.action) {
            case "add": {

                itemModel = findItemModel(data);

                if (itemModel) {
                    // Товар в корзине
                    itemModel.addQuantity(data.quantity);
                } else {
                    // Товар ещё не в корзине
                    itemModel = data.item;
                    if (itemModel) {
                        const product = itemModel.product;
                        itemModel = await addItem(itemModel.fields, product);
                    } else if (data.product || data.productId) {
                        const product = await ensureProduct(data.product || data.productId);
                        if (product) {
                            const item = fillBasketItemByProduct(
                                {QUANTITY: data.quantity},
                                product
                            );
                            itemModel = await addItem(item, product);
                        }
                    }
                }
                break;
            }
            case "add-multiple": {
                // Добавление нескольких товаров за одну операцию
                const productIds = data.products
                    .map(p => p.productId || p.product?.ID)
                    .filter(Boolean) as number[];

                // Загружаем все товары одним запросом
                const productsById = await ensureProductsById(productIds);

                // Добавляем каждый товар
                for (const productData of data.products) {
                    const productId = productData.productId || productData.product?.ID;
                    if (!productId) continue;

                    const product = productsById[productId];
                    if (!product) continue;

                    // Проверяем, есть ли уже такой товар в корзине
                    const existingItem = items.value.find(
                        item => item.fields.PRODUCT_ID === productId
                    );

                    if (existingItem) {
                        // Увеличиваем количество существующего товара
                        existingItem.addQuantity(productData.quantity);
                    } else {
                        // Добавляем новый товар
                        const item = fillBasketItemByProduct(
                            {QUANTITY: productData.quantity},
                            product
                        );
                        await addItem(item, product);
                    }
                }

                // Возвращаем null, т.к. добавлено несколько товаров
                itemModel = null;
                break;
            }
            case "quantity": {
                itemModel = findItemModel(data);
                if (itemModel) {
                    if (data.quantity > 0) {
                        itemModel.setQuantity(data.quantity);
                    } else {
                        itemModel.remove();
                    }
                }
                break;
            }
            case "delete": {
                itemModel = findItemModel(data);
                if (itemModel) itemModel.remove();
                break;
            }
            case "clear": {
                items.value = [];
                break;
            }
            case "remove-excess": {
                removeExcessItems();
                break;
            }
        }

        removeOfferItems();

        // Проверяем лимиты ПОСЛЕ всех операций
        // Только для операций которые могут изменить количество
        if (data.action === "quantity") {
            nextTick(() => removeExcessItems());
        }

        nextTick(onChangeDebounced);
        changedAt.value = Date.now();
        return itemModel;
    };

    const removeOfferItems = () => {
    };

    // WATCHERS

    watch(
        basketTotal,
        () => {
            checkGlobalModificatorsActivity();
        }
    );

    /**
     * Проверка активности глобальных модификаторов
     * Requirements: 3.3, 5.1
     *
     * Удаляет товары из неактивных глобальных модификаторов
     * Вызывается автоматически при изменении суммы корзины
     *
     * Расширено для поддержки динамических источников (prize/coupon/bonus):
     * - Проверяет наличие sourceEntity (активен ли источник)
     * - Проверяет соответствие товара текущему источнику
     * - Удаляет товары при деактивации источника
     */
    function checkGlobalModificatorsActivity() {

        const removed: string[] = [];

        for (const item of items.value) {
            // Проверяем только связанные товары
            if (!item.fields.PROPERTIES.REASON_MOD_ID) continue;
            if (item.getIsChild()) continue;

            const modId = item.fields.PROPERTIES.REASON_MOD_ID;
            const modificator = productModificatorsById.value[modId];

            // Проверяем только глобальные модификаторы
            if (!modificator || !modificator.IS_GLOBAL) continue;

            // НОВАЯ ПРОВЕРКА: Динамические источники (Requirements: 3.3, 5.1)
            const state = productModificatorsComputedStore.modificatorsStateMap[modId];

            if (state && state.productSource !== 'static') {
                // Проверяем, что источник еще активен
                if (!state.sourceEntity) {
                    removed.push(
                        (item.fields.NAME || "Товар") + " (бенефит больше не активен)"
                    );
                    item.remove();
                    continue;
                }

                // Проверяем, что товар соответствует текущему источнику
                const currentProductIds = state.products
                    .filter(p => p.source === 'dynamic')
                    .map(p => p.productId);
                if (!currentProductIds.includes(item.fields.PRODUCT_ID)) {
                    removed.push(
                        (item.fields.NAME || "Товар") + " (бенефит изменился)"
                    );
                    item.remove();
                    continue;
                }
            }

            // СУЩЕСТВУЮЩАЯ ПРОВЕРКА: ORDER_PRICE_FROM
            if (modificator.ORDER_PRICE_FROM) {
                // Проверяем активность модификатора
                const isInactive = basketTotal.value < modificator.ORDER_PRICE_FROM;

                if (isInactive) {
                    removed.push(
                        (item.fields.NAME || "Товар") + " (недостаточная сумма заказа)"
                    );
                    item.remove();
                }
            }
        }

        if (removed.length > 0) {
            showFlash({
                message:
                    "<div>Удалены:</div>" +
                    "<ul><li>" +
                    removed.join("</li><li>") +
                    "</li></ul>",
            });
        }
    }

    // EVENTS

    const onChange = () => {
        bus.emit("vorder:basket.change");
    }

    const onChangeDebounced = debounce(onChange, 100)

    return {
        basketTotal,
        basketAddItem: addItem,
        basketAddItemByRule: addItemByRule,
        basketRemoveItem: removeItem,
        basketRemoveItems: removeItems,
        basketGetChildren: getChildren,
        basketOp: op,
        fillBasket,
        itemsSource,
        items,
        basketItems: items,
        basketItemsRoot: itemsRoot,
        basketItemsWatchable: itemsWatchable,
        basketItemsModels: items,
        basketItemModelByProduct: itemByProduct,
        basketItemsModelsByProduct: itemsByProduct,

        basketPendingSync: pendingSync,
        basketOnChange: onChange,
        basketRemoveExcessItems: removeExcessItems,
        basketSync: sync,
        basketSave: save,
        basketFill: fill,
        basketSynced: synced,
        basketQueueSync: queueSync,
        basketQueueSave: queueSave,
        basketPrice: price,
        basketPriceLevel: priceLevel,
        basketIsEmpty: isEmpty,
        basketIsNotEmpty: isNotEmpty,
        basketWeight: weight,
        basketModificatorsStat: modificatorsStat,
        basketItemsStat,

    };

}, {
    persist: {
        //@ts-ignore
        pick: [
            'itemsSource',
        ],
    }
});
